import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Download, FileText, BarChart3, Target, Calendar, User, CheckCircle, XCircle, AlertCircle, TrendingUp, Lightbulb, Users, Award, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ResumeService, ResumeAnalysis } from "@/services/resumeService";
import { AppLayout } from "@/components/AppLayout";
import { PaywallGuard } from "@/components/PaywallGuard";

function ResumeAnalysisDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const [analysis, setAnalysis] = useState<ResumeAnalysis | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if this is from tailoring session with passed data
  const isFromTailoring = location.state?.isFromTailoring;
  const tailoringData = location.state;

  useEffect(() => {
    if (isFromTailoring && tailoringData) {
      // Use tailoring data directly instead of loading from database
      setLoading(false);
      return;
    }
    
    if (id && id !== 'tailoring-session') {
      loadAnalysisDetail();
    } else {
      setLoading(false);
    }
  }, [id, isFromTailoring]);

  const loadAnalysisDetail = async () => {
    if (!id || id === 'tailoring-session') return;
    
    try {
      const { analysis: analysisData, error } = await ResumeService.getResumeAnalysis(id);
      
      if (error) {
        throw error;
      }

      if (!analysisData) {
        toast({
          title: "Not Found",
          description: "Resume analysis not found",
          variant: "destructive"
        });
        navigate('/');
        return;
      }

      setAnalysis(analysisData);
    } catch (error) {
      console.error('Error loading analysis:', error);
      toast({
        title: "Error",
        description: "Failed to load resume analysis",
        variant: "destructive"
      });
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResume = async () => {
    if (!analysis) return;
    
    try {
      window.open(analysis.file_url, '_blank');
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the resume file",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 border-green-700 bg-green-50";
    if (score >= 56) return "text-yellow-600 border-yellow-700 bg-yellow-50";
    if (score >= 30) return "text-orange-600 border-orange-700 bg-orange-50";
    return "text-red-600 border-red-700 bg-red-50";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Excellent Match";
    if (score >= 56) return "Good Match";
    if (score >= 30) return "Fair Match";
    return "Poor Match";
  };

  const getQualificationStatus = (score: number) => {
    return score >= 70 ? "Congrats! Your Resume is Ready to Apply for This Job!" : "Your Resume is NOT Ready for You to Apply for This Job.";
  };

  const parseAnalysisContent = (analysisResults: any) => {
    if (!analysisResults) return null;

    // Extract different sections from the analysis
    const analysisText = analysisResults?.analysis || 
                        analysisResults?.summary || 
                        analysisResults?.report || 
                        (typeof analysisResults === 'string' ? analysisResults : '');

    // Parse structured data
    const skillsMatch = analysisResults?.skillsMatch || [];
    const experienceMatch = analysisResults?.experienceMatch || [];
    const requirementsMatch = analysisResults?.requirementsMatch || [];
    const responsibilitiesMatch = analysisResults?.responsibilitiesMatch || [];
    
    // Calculate individual scores
    const skillsScore = analysisResults?.skillsScore || 0;
    const experienceScore = analysisResults?.experienceScore || 0;
    const requirementsScore = analysisResults?.requirementsScore || 0;
    const atsScore = analysisResults?.atsScore || 0;

    return {
      analysisText,
      skillsMatch,
      experienceMatch,
      requirementsMatch,
      responsibilitiesMatch,
      skillsScore,
      experienceScore,
      requirementsScore,
      atsScore
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analysis...</p>
        </div>
      </div>
    );
  }

  // Handle tailoring session data
  if (isFromTailoring && tailoringData) {
    const analysisResults = tailoringData.tailoredAnalysis || tailoringData.originalAnalysis;
    const overallScore = analysisResults?.overallScore ?? null; // No fake scores - null if no real data
    const parsedAnalysis = parseAnalysisContent(analysisResults);

    // Create a mock analysis object for display
    const mockAnalysis = {
      id: 'tailoring-session',
      original_filename: 'Tailored Resume',
      created_at: new Date().toISOString(),
      job_title: tailoringData.jobTitle || 'Position Analysis',
      analysis_results: analysisResults,
      file_url: '#',
      extracted_text: tailoringData.tailoringResults?.Final_Resume_Text || '',
      job_description: tailoringData.tailoringResults?.job_description || ''
    };

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/resume-results')}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Tailoring Analysis Report</h1>
                  <p className="text-sm text-muted-foreground">Post-tailoring resume analysis</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-3xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight">
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Tailoring{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Analysis
                    </span>
                  </h1>
                  
                  <div className="max-w-3xl mx-auto px-4 mt-4">
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      Complete Resume Match Score Analysis After AI Tailoring
                    </p>
                  </div>
                </div>
              </div>

              {/* Steps Section - Clean Card Layout */}
              <div className="space-y-6">
              {/* Overall Score Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        {mockAnalysis.original_filename}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(mockAnalysis.created_at)}
                        </span>
                        {tailoringData.jobTitle && (
                          <span className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {tailoringData.jobTitle}
                          </span>
                        )}
                        {tailoringData.companyName && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {tailoringData.companyName}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    {overallScore !== null ? (
                      <div className={`w-24 h-24 rounded-full border-2 flex flex-col items-center justify-center ${getScoreColor(overallScore)}`}>
                        <div className="text-2xl font-bold">{overallScore}%</div>
                        <div className="text-xs font-medium">{getScoreLabel(overallScore)}</div>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full border-2 flex flex-col items-center justify-center border-muted bg-muted/10">
                        <div className="text-lg font-medium text-muted-foreground">N/A</div>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>

              {/* Resume Match Score Card */}
              <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
                <CardContent className="p-6">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                      <Target className="w-6 h-6 text-primary" />
                      Tailored Resume Analysis
                    </h2>
                    {overallScore !== null ? (
                      <div className="flex items-center justify-center gap-6 mb-6">
                        <div className={`text-5xl font-bold ${overallScore >= 80 ? 'text-green-600' : overallScore >= 56 ? 'text-yellow-600' : overallScore >= 30 ? 'text-orange-600' : 'text-red-600'}`}>
                          {overallScore}%
                        </div>
                        <div className="text-left">
                          <p className={`text-xl font-semibold ${overallScore >= 80 ? 'text-green-600' : overallScore >= 56 ? 'text-yellow-600' : overallScore >= 30 ? 'text-orange-600' : 'text-red-600'}`}>
                            {getScoreLabel(overallScore)}
                          </p>
                          <p className="text-muted-foreground">
                            Post-tailoring compatibility analysis
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-6 mb-6">
                        <div className="text-3xl font-bold text-muted-foreground">Score Unavailable</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Rest of the detailed analysis components... */}
              {/* Continue with the same structure but use tailoringData instead of analysis */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-blue-100 p-6">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <p className="text-muted-foreground">Analysis not found</p>
            <Button variant="outline" onClick={() => navigate('/?view=resume-library&tab=analyzed')} className="mt-4 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Resume Library
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const analysisResults: any = analysis.analysis_results 
    ? (typeof analysis.analysis_results === 'string' 
        ? JSON.parse(analysis.analysis_results) 
        : analysis.analysis_results)
    : null;

  // Extract match score - try multiple possible locations
  let matchScore = analysisResults?.overallScore || analysisResults?.match_score || 0;
  
  // If no score in structured data, try to extract from analysis text
  if (!matchScore && analysisResults?.analysis) {
    const analysisText = typeof analysisResults.analysis === 'string' ? analysisResults.analysis : JSON.stringify(analysisResults.analysis);
    const scoreMatch = analysisText.match(/(?:overall\s+resume\s+match\s+score|match\s+score(?:\s+is)?)\s*:?\s*(\d+)%/i);
    if (scoreMatch) {
      matchScore = parseInt(scoreMatch[1], 10);
    } else {
      const percentMatch = analysisText.match(/(\d+)%/);
      if (percentMatch) {
        matchScore = parseInt(percentMatch[1], 10);
      }
    }
  }

  const overallScore = matchScore || 0;
  const parsedAnalysis = parseAnalysisContent(analysisResults);

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/?view=resume-library&tab=analyzed')}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Resume Analysis</h1>
                  <p className="text-sm text-muted-foreground">Detailed resume compatibility report</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                  <span className="text-xs font-medium text-muted-foreground">powered by</span>
                  <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
                </div>
                <Button variant="outline" onClick={handleDownloadResume}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Resume
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-3xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight">
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Resume{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Analysis
                    </span>
                  </h1>
                  
                  <div className="max-w-3xl mx-auto px-4 mt-4">
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      Complete Resume Match Score Analysis & Detailed Breakdown
                    </p>
                  </div>
                </div>
              </div>

              {/* Steps Section - Clean Card Layout */}
              <div className="space-y-6">
            {/* Overall Score Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {analysis.original_filename}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(analysis.created_at)}
                      </span>
                      {analysis.job_title && (
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {analysis.job_title}
                        </span>
                      )}
                      {analysisResults?.applicantName && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {analysisResults.applicantName}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className={`w-24 h-24 rounded-full border-2 flex flex-col items-center justify-center ${getScoreColor(overallScore)}`}>
                    <div className="text-2xl font-bold">{overallScore}%</div>
                    <div className="text-xs font-medium">{getScoreLabel(overallScore)}</div>
                  </div>
                </div>
              </CardHeader>
              {analysisResults?.fitFeedback && (
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Overall Assessment</h4>
                    <p className="text-sm leading-relaxed">{analysisResults.fitFeedback}</p>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Resume Match Score Card */}
            <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                    <Target className="w-6 h-6 text-primary" />
                    Resume Match Score
                  </h2>
                  <div className="flex items-center justify-center gap-6 mb-6">
                    <div className={`text-5xl font-bold ${overallScore >= 80 ? 'text-green-600' : overallScore >= 56 ? 'text-yellow-600' : overallScore >= 30 ? 'text-orange-600' : 'text-red-600'}`}>
                      {overallScore}%
                    </div>
                    <div className="text-left">
                      <p className={`text-xl font-semibold ${overallScore >= 80 ? 'text-green-600' : overallScore >= 56 ? 'text-yellow-600' : overallScore >= 30 ? 'text-orange-600' : 'text-red-600'}`}>
                        {getScoreLabel(overallScore)}
                      </p>
                      <p className="text-muted-foreground">
                        Resume compatibility analysis
                      </p>
                    </div>
                  </div>

                  {/* Qualification Status */}
                  <div className={`rounded-lg border p-4 ${overallScore >= 70 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                    <h3 className={`text-lg font-bold ${overallScore >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                      {getQualificationStatus(overallScore)}
                    </h3>
                    {overallScore < 70 && (
                      <div className="mt-3">
                        <Button 
                          onClick={() => navigate('/resume-tailoring')}
                          className="bg-primary hover:bg-primary/90"
                        >
                          Use TailorWiz to Tailor it Now
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Introduction Card */}
            <Card className="bg-gradient-subtle border-border-light">
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <BarChart3 className="w-6 h-6" />
                    <h2 className="text-2xl font-bold">Detailed Resume Analysis Report</h2>
                  </div>
                  <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                    Your resume has been thoroughly analyzed against the target job requirements. This comprehensive report breaks down 
                    your match across multiple categories including skills, experience, requirements, and ATS optimization. Use these 
                    insights to understand your strengths and identify areas for improvement.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Category Scores Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Star className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Skills Match</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Score</span>
                      <span className="font-medium">{parsedAnalysis?.skillsScore || overallScore}%</span>
                    </div>
                    <Progress value={parsedAnalysis?.skillsScore || overallScore} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Award className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Experience</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Score</span>
                      <span className="font-medium">{parsedAnalysis?.experienceScore || overallScore}%</span>
                    </div>
                    <Progress value={parsedAnalysis?.experienceScore || overallScore} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Requirements</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Score</span>
                      <span className="font-medium">{parsedAnalysis?.requirementsScore || overallScore}%</span>
                    </div>
                    <Progress value={parsedAnalysis?.requirementsScore || overallScore} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">ATS Score</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Score</span>
                      <span className="font-medium">{parsedAnalysis?.atsScore || overallScore}%</span>
                    </div>
                    <Progress value={parsedAnalysis?.atsScore || overallScore} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Analysis Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Strengths Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    Strengths & Matches
                  </CardTitle>
                  <CardDescription>
                    Areas where your resume aligns well with the job requirements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {overallScore >= 70 ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2">Strong Overall Match</h4>
                      <p className="text-sm text-green-700">
                        Your resume demonstrates excellent alignment with the job requirements. Key strengths include:
                      </p>
                      <ul className="text-sm text-green-700 mt-2 space-y-1">
                        <li>• Relevant skills and experience clearly presented</li>
                        <li>• Professional formatting and structure</li>
                        <li>• Good keyword optimization for ATS systems</li>
                      </ul>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-yellow-600">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Partial Matches Found</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Your resume shows some alignment with the position, but there are opportunities to strengthen the match.
                      </p>
                    </div>
                  )}
                  
                  {analysisResults?.jobSkills && analysisResults.jobSkills.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Matched Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {analysisResults.jobSkills.slice(0, 8).map((skill: string, index: number) => (
                          <Badge key={index} variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Improvement Areas Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-600">
                    <TrendingUp className="w-5 h-5" />
                    Areas for Improvement
                  </CardTitle>
                  <CardDescription>
                    Opportunities to strengthen your resume's match with this position
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {overallScore < 70 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <h4 className="font-semibold text-orange-800 mb-2">Enhancement Opportunities</h4>
                      <p className="text-sm text-orange-700 mb-2">
                        To improve your match score, consider:
                      </p>
                      <ul className="text-sm text-orange-700 space-y-1">
                        <li>• Adding more relevant keywords from the job description</li>
                        <li>• Highlighting transferable skills and experiences</li>
                        <li>• Quantifying achievements with specific metrics</li>
                        <li>• Tailoring your professional summary</li>
                      </ul>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="font-medium mb-2">Optimization Tips</h4>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 mt-0.5 text-yellow-500" />
                        <span>Use industry-specific terminology from the job posting</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 mt-0.5 text-yellow-500" />
                        <span>Include measurable results and achievements</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 mt-0.5 text-yellow-500" />
                        <span>Ensure consistent formatting throughout</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actionable Insights */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Next Steps & Recommendations
                </CardTitle>
                <CardDescription>
                  Actionable steps to improve your resume's effectiveness
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Immediate Actions</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
                        <span>Review job description for additional keywords to incorporate</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
                        <span>Quantify achievements with specific numbers and percentages</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
                        <span>Ensure consistent verb tenses and formatting</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold">Long-term Improvements</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
                        <span>Develop skills mentioned in the job requirements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
                        <span>Gain relevant certifications or training</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
                        <span>Seek projects that demonstrate required competencies</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {overallScore < 70 && (
                  <div className="mt-6 p-4 bg-white rounded-lg border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-primary">Want to improve your match score?</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use our TailorWiz tool to automatically optimize your resume for this specific job.
                        </p>
                      </div>
                      <Button 
                        onClick={() => navigate('/resume-tailoring')}
                        className="ml-4"
                      >
                        Try TailorWiz
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Raw Analysis Data (Collapsible) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Technical Analysis Details
                </CardTitle>
                <CardDescription>
                  Raw analysis data and source content for reference
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="summary" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="summary">Analysis Summary</TabsTrigger>
                    <TabsTrigger value="job-description">Job Description</TabsTrigger>
                    <TabsTrigger value="resume-content">Resume Content</TabsTrigger>
                  </TabsList>

                  <TabsContent value="summary" className="space-y-4">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-3">Complete Analysis Report</h4>
                      <div className="bg-muted/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div className="prose prose-sm max-w-none">
                          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                            {parsedAnalysis?.analysisText || "No detailed analysis available"}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="job-description" className="space-y-4">
                    {analysis.job_description ? (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Target Job Description</h4>
                        <div className="bg-muted/50 rounded p-4 max-h-96 overflow-y-auto">
                          <pre className="text-sm whitespace-pre-wrap font-sans">{analysis.job_description}</pre>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No job description available for this analysis</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="resume-content" className="space-y-4">
                    {analysis.extracted_text ? (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Resume Content</h4>
                        <div className="bg-muted/50 rounded p-4 max-h-96 overflow-y-auto">
                          <pre className="text-sm whitespace-pre-wrap font-sans">{analysis.extracted_text}</pre>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Resume content not available</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main export with PaywallGuard
export default function ResumeAnalysisDetail() {
  return (
    <PaywallGuard feature="Resume Analysis">
      <ResumeAnalysisDetailContent />
    </PaywallGuard>
  );
}